import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { preloadTexture } from "@/components/shop/ChromaPhoto";
import { ProductDesigner } from "@/components/shop/ProductDesigner";
import { getProduct, getProductPatterns, textureSrc } from "@/lib/catalog";
import { isSoldOut } from "@/lib/patternAvailability";
import { diversePicks } from "@/lib/patternFamilies";
import { Reveal } from "./motion";
import { StitchWave } from "./StitchWave";

// "E se fosse neste padrão?": o porta-chupetas, desenhado ao vivo no tecido escolhido, e uma fila de amostras.
// Tocar (ou clicar) numa amostra aplica o tecido às orelhas e à gola. Nada depende de hover: o toque funciona sempre.
// Fundo: waffle rosa bebé com um véu creme (é o mesmo padrão que forma a onda de cima).
const BG = "linear-gradient(rgba(249,240,235,.62), rgba(249,240,235,.62)), url(/img/demo/waffle-rosa-bebe-flat-800.webp)";
const BG_SIZE = "auto, 190px";

export function WhatIfFabric() {
  const product = getProduct("porta-chupetas");
  const picks = product ? diversePicks(getProductPatterns(product).filter((p) => !isSoldOut(p)), 14) : [];
  const [sel, setSel] = useState(picks[0]?.id);
  const root = useRef(null);

  // Prepara os tecidos quando a secção se aproxima, para a troca ser instantânea.
  useEffect(() => {
    const el = root.current;
    if (!el || !picks.length || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        picks.forEach((p) => preloadTexture(p.texture));
        io.disconnect();
      }
    }, { rootMargin: "500px 0px" });
    io.observe(el);
    return () => io.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!product || !picks.length) return null;
  const cur = picks.find((p) => p.id === sel) ?? picks[0];

  return (
    <section ref={root} className="relative py-9 sm:py-14 lg:py-16" style={{ background: BG, backgroundSize: BG_SIZE }} data-testid="home-whatif">
      <StitchWave background={BG} bgSize={BG_SIZE} />
      <div className="mx-auto grid max-w-[1360px] grid-cols-[minmax(0,1fr)] gap-5 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:content-center lg:gap-x-14 lg:gap-y-6">
        {/* texto e amostras */}
        <div className="px-5 sm:px-0 lg:col-start-1 lg:row-start-1 lg:self-end">
          <Reveal y={14}>
            <p className="bb-eyebrow mb-3 hidden sm:block">Todos os padrões, a mesma peça</p>
            <h2 className="bb-h2 text-ink" style={{ fontSize: "clamp(1.8rem, 3.6vw, 3.2rem)", lineHeight: 1.05 }}>
              E se fosse <em className="bb-accent text-bordeaux">neste padrão?</em>
            </h2>
          </Reveal>
        </div>

        {/* o porta-chupetas, desenhado ao vivo no tecido escolhido */}
        <div className="relative lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <div className="relative aspect-[5/4] w-full sm:aspect-[5/4] lg:aspect-[4/3.3]" data-testid="whatif-preview">
            <ProductDesigner product={product} texture={cur.texture} patternName={cur.name} name="" />
          </div>
        </div>

        <div className="space-y-2 px-5 pb-2 sm:space-y-5 sm:px-0 sm:pb-0 lg:col-start-1 lg:row-start-2 lg:self-start">
          <div className="-mx-5 flex snap-x gap-3 overflow-x-auto px-6 py-2 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-1 [&::-webkit-scrollbar]:hidden" role="group" aria-label="Escolher o padrão">
            {picks.map((p) => {
              const on = p.id === cur.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSel(p.id)}
                  aria-pressed={on}
                  aria-label={`Aplicar o padrão ${p.name}`}
                  data-testid={`whatif-swatch-${p.id}`}
                  className={`h-14 w-14 shrink-0 snap-start rounded-full bg-cover bg-center transition-all duration-300 sm:h-[60px] sm:w-[60px] ${on ? "ring-2 ring-bordeaux ring-offset-2 ring-offset-linen" : "opacity-85 hover:opacity-100"}`}
                  style={{ backgroundImage: `url(${textureSrc(p.texture, 240)})` }}
                />
              );
            })}
          </div>
          <Link to={`/produto/porta-chupetas?tecido=${cur.id}`} data-testid="whatif-cta" className="bb-link bb-tap gap-2 text-[15px] font-medium text-bordeaux">
            Experimentar todos os padrões <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
